import { useParams, Link, Navigate } from 'react-router-dom';
import {
  SparklesIcon,
  BeakerIcon,
  ChatBubbleLeftRightIcon,
  DocumentChartBarIcon,
  MagnifyingGlassIcon,
  LightBulbIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  CpuChipIcon,
  CheckIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  MapIcon,
  ScaleIcon,
  ShieldCheckIcon,
  ArrowTrendingDownIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import { FadeIn, StaggeredFadeIn } from '../components/animations/FadeIn';
import { Seo } from '../components/Seo';

interface AgentCapability {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface ConversationTurn {
  role: 'user' | 'agent';
  text: string;
}

interface Agent {
  slug: string;
  name: string;
  tagline: string;
  icon: React.ComponentType<{ className?: string }>;
  status: 'available' | 'coming_soon';
  description: string;
  capabilities: AgentCapability[];
  conversation: { context: string; turns: ConversationTurn[] };
  useCases: string[];
  technicalSpecs: { label: string; value: string }[];
  accent: { bg: string; border: string; text: string; gradient: string; button: string; glow: string };
}

const ACCENTS = {
  cyan: {
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/30',
    text: 'text-cyan-400',
    gradient: 'from-cyan-500/20 to-cyan-600/5',
    button: 'bg-cyan-600 hover:bg-cyan-500',
    glow: 'border-cyan-400/20 shadow-[0_0_50px_-22px_rgba(34,211,238,0.45)] hover:border-cyan-400/40 hover:shadow-[0_0_55px_-16px_rgba(34,211,238,0.6)]',
  },
  emerald: {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    text: 'text-emerald-400',
    gradient: 'from-emerald-500/20 to-emerald-600/5',
    button: 'bg-emerald-600 hover:bg-emerald-500',
    glow: 'border-emerald-400/20 shadow-[0_0_50px_-22px_rgba(52,211,153,0.45)] hover:border-emerald-400/40 hover:shadow-[0_0_55px_-16px_rgba(52,211,153,0.6)]',
  },
};

const AGENTS: Record<string, Agent> = {
  terra: {
    slug: 'terra',
    name: 'TERRA',
    tagline: 'Technical Environmental Research and Reasoning Agent',
    icon: SparklesIcon,
    status: 'available',
    accent: ACCENTS.cyan,
    description:
      'A senior-level environmental scientist on demand — not a chatbot, not an API call. TERRA is trained across the full body of environmental and contaminated-site data: decades of remediation case histories, EPA and state regulatory codes, laboratory analytical methods, and field investigation records. It reasons across your full Polygon and Data Vault corpus the way an experienced consultant would — weighs forecasts against regulatory limits, ranks site priorities, drafts defensible narratives, and shows its work with source citations. The depth of a multi-day analysis, returned in seconds.',
    capabilities: [
      {
        title: 'Natural-Language Site Q&A',
        description:
          'Ask anything about a site — "what is the BTEX trajectory at MW-12?" "when does plume reach the property boundary?" — and get answers grounded in actual model output and historical data.',
        icon: ChatBubbleLeftRightIcon,
      },
      {
        title: 'Forecast Synthesis',
        description:
          'Pulls ensemble predictions, plume polygons, cost-engine output, and delineation recommendations into a single coherent narrative per site.',
        icon: ChartBarIcon,
      },
      {
        title: 'Regulatory Drafting',
        description:
          'Generates first-draft language for TCEQ, EPA, and state regulatory submissions — pulling from the active regulatory library and the site\'s current concentration data.',
        icon: DocumentChartBarIcon,
      },
      {
        title: 'Cross-Product Search',
        description:
          'Reasons across Polygon predictions, Data Vault documents, and regulatory references simultaneously. One question, all sources.',
        icon: MagnifyingGlassIcon,
      },
      {
        title: 'Document Intelligence',
        description:
          'Surfaces relevant past site reports, lab packages, and historical context to ground every answer in evidence — with citations back to source documents.',
        icon: ClipboardDocumentListIcon,
      },
      {
        title: 'Decision Support',
        description:
          'Compares remediation system options against forecasted plume behavior and projected cost-of-delay. Quantifies trade-offs before you commit capital.',
        icon: LightBulbIcon,
      },
    ],
    useCases: [
      'Answer client questions about site status and trajectory in seconds',
      'Draft regulatory submission narratives from raw site data',
      'Synthesize quarterly portfolio reports across hundreds of sites',
      'Triage which sites need immediate attention based on forecast trajectory',
      'Compare remediation strategies against predicted plume + cost outcomes',
      'Audit document corpora for missing data or inconsistent reporting',
    ],
    technicalSpecs: [
      { label: 'Expertise', value: 'Senior-level environmental scientist — peer-grade reasoning, not transactional Q&A' },
      { label: 'Training', value: 'Decades of contaminated-site records, EPA + state regulatory codes, lab analytical methods, and remediation case histories' },
      { label: 'Data Surfaces', value: 'Polygon predictions + Data Vault corpus + regulatory library' },
      { label: 'Citations', value: 'Grounded answers with source attribution' },
      { label: 'Access', value: 'Embedded panel + standalone chat + API' },
    ],
    conversation: {
      context: 'Polygon site — Demo Pipeline No. 7',
      turns: [
        {
          role: 'user',
          text: "What's the benzene situation on this site — concentration and where it's headed?",
        },
        {
          role: 'agent',
          text:
            'Benzene is the driving contaminant. Site concentration reads 0.749 mg/L against the ' +
            '0.005 mg/L EPA MCL — a 150× exceedance. 25 wells exceed today, and the ensemble ' +
            'keeps 24 of them hot at the 20-year horizon. Initial PSH was 58.7 lbs and is already ' +
            '70.6% depleted. Start-Now remediation runs $12.03M; wait, and the cost-of-delay ' +
            'settles at $3.66M.',
        },
        {
          role: 'user',
          text: 'Does the dissolved plume reach the property boundary?',
        },
        {
          role: 'agent',
          text:
            'Not at the regulatory MCL. The rehulled 10-year plume polygon — peak dissolved ' +
            'footprint 672,015 sq ft — stops short of the eastern boundary. To close out the ' +
            'northeast flank, Delineation Intelligence recommends 11 monitoring wells at $306K ' +
            'total, ranked by future-horizon exceedance. Want me to draft the TCEQ narrative ' +
            'from this?',
        },
      ],
    },
  },
  cari: {
    slug: 'cari',
    name: 'CARI',
    tagline: 'Comprehensive Aquifer Reasoning and Intelligence',
    icon: BeakerIcon,
    status: 'available',
    accent: ACCENTS.cyan,
    description:
      'A senior-level hydrogeologist on demand — not a chatbot, not an API call. CARI is trained across the full body of groundwater and water science: well inventories, drawdown histories, recharge records, water-quality datasets, hydrogeologic literature, and TCEQ and GCD case files. It reasons about a parcel the way a working hydrogeologist would — matches every well to the right aquifer, weighs sustainable yield against demand, flags drought-resilience risks, and writes lending-grade recommendations with the receipts to back them up. The depth of a week of fieldwork, returned in seconds.',
    capabilities: [
      {
        title: 'Aquifer Q&A',
        description:
          'Ask "how much sustainable yield does this parcel have?" "what is the drawdown risk over 20 years?" and get answers backed by V5 forecasts and the spatial aquifer classifier.',
        icon: ChatBubbleLeftRightIcon,
      },
      {
        title: 'Per-Aquifer Reasoning',
        description:
          'Knows the difference between Edwards, Ogallala, Gulf Coast, and Dockum — and routes every question to the right V5 model and hydraulic-K substrate.',
        icon: MapIcon,
      },
      {
        title: 'Lending Risk Drafting',
        description:
          'Generates bank-ready risk write-ups for property transactions. Pulls aquifer assessment, supply analysis, drought resilience, and water quality into a single recommendation.',
        icon: BanknotesIcon,
      },
      {
        title: 'TCEQ 230 + GCD Reasoning',
        description:
          'Knows which jurisdictions apply, what permits are required, and what the approval likelihood looks like for a given parcel.',
        icon: ScaleIcon,
      },
      {
        title: 'Drawdown Forecasting',
        description:
          'Synthesizes cone-of-depression, sustainable-yield, and cavitation-risk outputs into a long-horizon drawdown narrative per well.',
        icon: ArrowTrendingDownIcon,
      },
      {
        title: 'Floyd Factor Synthesis',
        description:
          'Surfaces the proprietary Floyd Factor aquifer-health metric alongside the drivers — drawdown history, recharge variance, K — so non-experts understand what changed.',
        icon: ShieldCheckIcon,
      },
    ],
    useCases: [
      'Agricultural lenders verifying groundwater for property transactions',
      'Water utilities assessing long-term sustainable yield',
      'Data center site selection — verify cooling water availability before breaking ground',
      'Ranch and farmland acquisition due diligence',
      'GCD permit application support with regulatory and yield reasoning',
      'Drought-resilience scoring for property portfolios',
    ],
    technicalSpecs: [
      { label: 'Expertise', value: 'Senior-level hydrogeologist — peer-grade reasoning, not transactional Q&A' },
      { label: 'Training', value: 'Texas groundwater and aquifer science — well inventories, drawdown histories, recharge records, water-quality data, TCEQ and GCD case files' },
      { label: 'Data Surfaces', value: 'AI aquifer forecasts + spatial classifier + TCEQ/GCD records' },
      { label: 'Aquifer Coverage', value: 'Edwards / Ogallala / Gulf Coast / Dockum / General' },
      { label: 'Output Format', value: 'Plain-English answers + lending-grade reports' },
    ],
    conversation: {
      context: 'Phreatic site — Demo parcel, Ogallala aquifer',
      turns: [
        {
          role: 'user',
          text: "What's the saturated thickness on this parcel, and how long will it hold?",
        },
        {
          role: 'agent',
          text:
            'The parcel sits over the Ogallala. Average saturated thickness across the three ' +
            'on-site wells is 147 ft today. The V5 Ogallala bundle projects it declining to ' +
            '131 ft at the 10-year horizon and 118 ft at 20 years under current demand — about ' +
            '1.45 ft/yr. Every well stays above pump submergence through the full horizon.',
        },
        {
          role: 'user',
          text: 'Is that enough to supply a 100,000-head cattle feedyard?',
        },
        {
          role: 'agent',
          text:
            'Yes. That feedyard demands roughly 1,667 GPM. Sustainable yield on this parcel is ' +
            '6,424 GPM against a 20-year target — 4,758 GPM of headroom. Drawdown risk is low ' +
            'and the Floyd Factor reads healthy. One caveat for the lending memo: the Ogallala ' +
            'has minimal recharge here, so the 20-year decline is effectively permanent — ' +
            'budget around the 118 ft endpoint.',
        },
      ],
    },
  },
};

export function AgentDetail() {
  const { slug } = useParams<{ slug: string }>();
  const agent = slug ? AGENTS[slug] : undefined;

  if (!agent) {
    return <Navigate to="/" replace />;
  }

  const Icon = agent.icon;
  const { accent } = agent;

  return (
    <div className="min-h-screen bg-mono-950">
      <Seo
        title={`${agent.name} — ${agent.tagline} | Argos Tellus`}
        description={agent.description}
        path={`/agents/${agent.slug}`}
      />
      {/* Header */}
      <header className="border-b border-mono-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <FadeIn direction="left" delay={0} duration={500}>
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-mono-500 hover:text-mono-300 transition-colors">
                <ArrowLeftIcon className="h-4 w-4" />
                <span className="text-xs tracking-widest">HOME</span>
              </Link>
              <span className="text-mono-700">/</span>
              <span className="text-xl font-bold text-mono-100 tracking-[0.3em]">{agent.name}</span>
            </div>
          </FadeIn>
          <FadeIn direction="right" delay={100} duration={500}>
            <Link
              to="/login"
              className="px-4 py-2 border border-mono-600 text-mono-300 text-xs tracking-widest hover:border-mono-400 transition-colors"
            >
              LOGIN
            </Link>
          </FadeIn>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-mono-800">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <FadeIn delay={100} duration={600}>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`p-4 ${accent.bg} rounded-lg border ${accent.border}`}>
                    <Icon className={`h-10 w-10 ${accent.text}`} />
                  </div>
                  <div>
                    <p className={`text-xs ${accent.text} tracking-[0.3em]`}>YOUR ASSISTANT</p>
                  </div>
                </div>
              </FadeIn>
              <FadeIn delay={200} duration={600}>
                <h1 className="text-4xl lg:text-5xl font-bold text-mono-100 tracking-wide leading-tight">
                  {agent.name}
                </h1>
              </FadeIn>
              <FadeIn delay={250} duration={600}>
                <p className={`mt-3 text-base ${accent.text} tracking-wide`}>{agent.tagline}</p>
              </FadeIn>
              <FadeIn delay={300} duration={600}>
                <p className="mt-6 text-mono-300 text-base leading-relaxed">{agent.description}</p>
              </FadeIn>
              {agent.status === 'coming_soon' && (
                <FadeIn delay={400} duration={600}>
                  <div className="mt-6 inline-block px-3 py-1 border border-mono-700 text-xs text-mono-400 tracking-widest rounded">
                    COMING SOON
                  </div>
                </FadeIn>
              )}
            </div>

            {/* Technical Specs Card */}
            <FadeIn delay={300} duration={600}>
              <div className={`border ${accent.border} rounded-lg p-8 bg-gradient-to-br ${accent.gradient}`}>
                <p className="text-[10px] text-mono-500 tracking-[0.3em] mb-6">TECHNICAL SPECIFICATIONS</p>
                <div className="space-y-4">
                  {agent.technicalSpecs.map((spec) => (
                    <div
                      key={spec.label}
                      className="flex items-start justify-between gap-4 py-3 border-b border-mono-800 last:border-0"
                    >
                      <span className="text-sm text-mono-400">{spec.label}</span>
                      <span className="text-sm text-mono-100 font-medium text-right">{spec.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="border-b border-mono-800">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <FadeIn delay={0} duration={500}>
            <p className="text-[10px] text-mono-500 tracking-[0.4em] mb-4">CAPABILITIES</p>
          </FadeIn>
          <FadeIn delay={100} duration={500}>
            <h2 className="text-2xl font-bold text-mono-100 tracking-wide mb-12">
              WHAT {agent.name} CAN DO
            </h2>
          </FadeIn>
          <StaggeredFadeIn
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            staggerDelay={80}
          >
            {agent.capabilities.map((cap) => {
              const CapIcon = cap.icon;
              return (
                <div
                  key={cap.title}
                  className={`rounded-2xl border bg-gradient-to-br from-mono-900 to-mono-950 p-6 h-full flex flex-col transition-all duration-300 ${accent.glow}`}
                >
                  <CapIcon className={`h-6 w-6 ${accent.text} mb-4`} />
                  <h3 className="text-xs font-medium text-mono-200 tracking-widest mb-3">
                    {cap.title.toUpperCase()}
                  </h3>
                  <p className="text-xs text-mono-500 leading-relaxed flex-grow">{cap.description}</p>
                </div>
              );
            })}
          </StaggeredFadeIn>
        </div>
      </section>

      {/* Sample Conversation */}
      <section className="border-b border-mono-800">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <FadeIn delay={0} duration={500}>
            <p className="text-[10px] text-mono-500 tracking-[0.4em] mb-4">IN CONVERSATION</p>
          </FadeIn>
          <FadeIn delay={100} duration={500}>
            <h2 className="text-2xl font-bold text-mono-100 tracking-wide mb-3">
              A TYPICAL EXCHANGE
            </h2>
          </FadeIn>
          <FadeIn delay={150} duration={500}>
            <p className="text-sm text-mono-400 mb-10">{agent.conversation.context}</p>
          </FadeIn>
          <FadeIn delay={200} duration={600}>
            <div
              className={`max-w-3xl mx-auto border ${accent.border} rounded-xl bg-gradient-to-br ${accent.gradient} overflow-hidden`}
            >
              {/* Chat header */}
              <div className="flex items-center gap-3 px-5 py-4 border-b border-mono-800 bg-mono-900/60">
                <div className={`p-2 ${accent.bg} rounded-lg border ${accent.border}`}>
                  <Icon className={`h-4 w-4 ${accent.text}`} />
                </div>
                <span className="text-xs font-semibold text-mono-200 tracking-[0.2em]">
                  {agent.name}
                </span>
                <span className="ml-auto flex items-center gap-1.5">
                  <span className={`h-1.5 w-1.5 rounded-full ${accent.text} bg-current`} />
                  <span className="text-[10px] text-mono-500 tracking-widest">SIMULATED</span>
                </span>
              </div>
              {/* Messages */}
              <div className="p-5 sm:p-6 space-y-4 bg-mono-950/40">
                {agent.conversation.turns.map((turn, i) => (
                  <div
                    key={i}
                    className={`flex ${turn.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={
                        turn.role === 'user'
                          ? 'max-w-[80%] rounded-2xl rounded-br-sm bg-mono-800 px-4 py-3'
                          : `max-w-[88%] rounded-2xl rounded-bl-sm border ${accent.border} ${accent.bg} px-4 py-3`
                      }
                    >
                      {turn.role === 'agent' && (
                        <span className={`block text-[10px] ${accent.text} tracking-[0.2em] mb-1.5`}>
                          {agent.name}
                        </span>
                      )}
                      <p className="text-sm text-mono-200 leading-relaxed">{turn.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Use Cases */}
      <section className="border-b border-mono-800">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <FadeIn delay={0} duration={500}>
                <p className="text-[10px] text-mono-500 tracking-[0.4em] mb-4">APPLICATIONS</p>
              </FadeIn>
              <FadeIn delay={100} duration={500}>
                <h2 className="text-2xl font-bold text-mono-100 tracking-wide mb-6">IDEAL USE CASES</h2>
              </FadeIn>
              <FadeIn delay={200} duration={500}>
                <p className="text-sm text-mono-400 leading-relaxed">
                  {agent.description.split('.')[0]}.
                </p>
              </FadeIn>
            </div>
            <FadeIn delay={300} duration={600}>
              <div className="space-y-4">
                {agent.useCases.map((useCase, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-4 p-4 border ${accent.border} rounded-lg bg-gradient-to-r ${accent.gradient}`}
                  >
                    <span className={`text-lg font-bold ${accent.text}`}>{index + 1}</span>
                    <p className="text-sm text-mono-300">{useCase}</p>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-mono-800">
        <FadeIn delay={0} duration={600}>
          <div className="max-w-7xl mx-auto px-6 py-20 text-center">
            <h2 className="text-2xl font-bold text-mono-100 tracking-wide mb-4">
              {agent.status === 'available'
                ? `READY TO TALK TO ${agent.name}?`
                : `BE THE FIRST TO TRY ${agent.name}.`}
            </h2>
            <p className="text-xs text-mono-400 tracking-wide mb-8">
              {agent.status === 'available'
                ? 'SIGN IN TO ACCESS THE AGENT INSIDE YOUR WORKSPACE'
                : 'GET NOTIFIED WHEN CARI ENTERS BETA'}
            </p>
            <Link
              to={agent.status === 'available' ? '/login' : '/contact-sales'}
              className={`inline-block px-8 py-3 border ${accent.border} ${accent.text} text-xs tracking-widest hover:bg-white/5 transition-colors`}
            >
              {agent.status === 'available' ? 'GO TO WORKSPACE' : 'NOTIFY ME'}
            </Link>
          </div>
        </FadeIn>
      </section>

      {/* Footer */}
      <footer>
        <FadeIn delay={0} duration={400}>
          <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between">
            <div>
              <span className="text-sm font-bold text-mono-400 tracking-[0.2em]">ARGOS</span>
              <span className="text-[10px] text-mono-600 tracking-wider ml-2">
                ENVIRONMENTAL AUTOMATION
              </span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-[10px] text-mono-500 tracking-wider hover:text-mono-300">
                HOME
              </Link>
              <Link to="/login" className="text-[10px] text-mono-500 tracking-wider hover:text-mono-300">
                LOGIN
              </Link>
              <Link
                to="/agents/terra"
                className={`text-[10px] tracking-wider ${slug === 'terra' ? 'text-cyan-400' : 'text-mono-500 hover:text-mono-300'}`}
              >
                TERRA
              </Link>
              <Link
                to="/agents/cari"
                className={`text-[10px] tracking-wider ${slug === 'cari' ? 'text-cyan-400' : 'text-mono-500 hover:text-mono-300'}`}
              >
                CARI
              </Link>
            </div>
          </div>
        </FadeIn>
      </footer>
    </div>
  );
}
